import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import {
  hasNativeAppBridge,
  isProbablyWebViewUA
} from '@/shared/lib/runtime/detect'
import type { RenderContextKind } from '@/types/render-context'

type Ctx = {
  context: RenderContextKind
  inIframe: boolean
  inWebView: boolean
  inNativeAppWebView: boolean
  ready: boolean
  referrer: string | null
}

const RenderCtx = createContext<Ctx>({
  context: 'browser',
  inIframe: false,
  inWebView: false,
  inNativeAppWebView: false,
  ready: true,
  referrer: null
})

type ProviderProps = {
  children: React.ReactNode
  initialUserAgent?: string
  initialIsIframe?: boolean
  fallback?: React.ReactNode
}

export function RenderContextProvider({
  children,
  initialUserAgent,
  initialIsIframe,
  fallback
}: ProviderProps) {
  const getInitialFromUA = (ua?: string): RenderContextKind =>
    isProbablyWebViewUA(ua) ? 'webview' : 'browser'

  const initial = useMemo<RenderContextKind>(() => {
    const fromUA = getInitialFromUA(initialUserAgent)
    if (typeof initialIsIframe === 'boolean') {
      if (initialIsIframe && fromUA === 'webview') return 'iframe_webview'
      if (initialIsIframe && fromUA !== 'webview') return 'iframe'
    }
    return fromUA
  }, [initialUserAgent, initialIsIframe])

  const [context, setContext] = useState<RenderContextKind>(
    initial || 'browser'
  )
  const [referrer, setReferrer] = useState<string | null>(null)
  const [inNativeAppWebView, setInNativeAppWebView] = useState(false)
  const [ready, setReady] = useState(true)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const isInIframeSafe = (): boolean | 'unknown' => {
      try {
        return window.self !== window.top
      } catch {
        return 'unknown'
      }
    }

    const inIframe = isInIframeSafe()
    const ua = navigator.userAgent || (navigator as any).vendor || ''
    const webViewUA = isProbablyWebViewUA(ua)
    const webViewHints = hasNativeAppBridge(window)
    const inWebView = webViewUA || webViewHints

    let finalContext: RenderContextKind = 'browser'
    if (inIframe === true && inWebView) finalContext = 'iframe_webview'
    else if (inIframe === true) finalContext = 'iframe'
    else if (inWebView) finalContext = 'webview'
    else if (inIframe === 'unknown') finalContext = 'unknown'
    else finalContext = 'browser'

    setContext(finalContext)
    setInNativeAppWebView(webViewHints)
    setReferrer(document.referrer)
    setReady(true)

    document.documentElement.setAttribute('data-render-context', finalContext)
  }, [])

  const value: Ctx = useMemo(
    () => ({
      context,
      inIframe: context === 'iframe' || context === 'iframe_webview',
      inWebView: context === 'webview' || context === 'iframe_webview',
      inNativeAppWebView,
      ready,
      referrer
    }),
    [context, inNativeAppWebView, ready, referrer]
  )

  return <RenderCtx.Provider value={value}>{children}</RenderCtx.Provider>
}

export function useRenderContext() {
  return useContext(RenderCtx)
}
